import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Modal,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { isDarkMode } from "../../utils/constants";
import { useAppContext } from "../../contexts/AppContext";
import { chatThreadModalMessage } from "../../utils/constants";
import { useChatContext } from "../../contexts/ChatContext";
import { isMobile } from "react-device-detect";
import { isAdmin } from "../../pages/Admin/utils";
import { ComingSoonBadge } from "./utils";
import { adminCreateTargetedThread } from "../../api/message";
import { useNavigate } from "react-router-dom";
import UpgradeMaxModal from "./UpgradeMaxModal";

const SidebarAction = ({
  onQuestionSubmit,
  handleAddThread = () => {},
  preAccountUser,
  hasSubscription,
  openWatchlist,
  openTradeProposals,
  openMemory,
  selectActiveThread = () => {},
  setSidebarScrollToThreadUuid = () => {},
  canSwitchThreads = true,
}) => {
  const getMessageSnippet = (message, query, maxLen = 160) => {
    if (!message) return "";
    const normalized = message.replace(/[*#]+/g, "");
    if (!query) return normalized;
    const lowerMessage = normalized.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const matchIndex = lowerMessage.indexOf(lowerQuery);
    if (matchIndex === -1) return normalized;
    const isWordChar = (ch) => /[A-Za-z0-9_]/.test(ch);
    let wordStart = matchIndex;
    while (wordStart > 0 && isWordChar(normalized[wordStart - 1])) {
      wordStart -= 1;
    }
    let wordEnd = matchIndex + lowerQuery.length;
    while (wordEnd < normalized.length && isWordChar(normalized[wordEnd])) {
      wordEnd += 1;
    }
    const sliceStart = wordStart;
    const sliceEnd = Math.min(sliceStart + maxLen, normalized.length);
    return normalized.slice(sliceStart, sliceEnd);
  };

  const { appProps, userData } = useAppContext();
  const {
    setIsSidebarOpen,
    threads,
    setThreads,
    setScrollToMessageId,
    loadingResponse,
    activeThread,
  } = useChatContext();
  const { setPanel } = appProps;
  const navigate = useNavigate();

  const { setShowSignUpModal, setShowPaywallModal, setPaywallMessage } =
    appProps;
  const [searchQuery, setSearchQuery] = useState("");
  const [messageResults, setMessageResults] = useState([]);
  const isAdminUser = isAdmin(userData);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [targetSearch, setTargetSearch] = useState("");
  const [targetResults, setTargetResults] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [targetMessage, setTargetMessage] = useState("");
  const [targetTitle, setTargetTitle] = useState("");
  const [targetError, setTargetError] = useState("");
  const [targetSending, setTargetSending] = useState(false);
  const [targetCreateAdminCopy, setTargetCreateAdminCopy] = useState(true);
  const [showThreadSwitchModal, setShowThreadSwitchModal] = useState(false);

  const handleOpenSignUpModal = () => {
    if (isMobile) {
      setPanel("chat"); // so that it doesn't show back arrow when closed
      setIsSidebarOpen(false);
    }
    setShowSignUpModal(true);
  };

  const handleOpenPaywallModal = () => {
    if (isMobile) {
      setPanel("chat"); // so that it doesn't show back arrow when closed
      setIsSidebarOpen(false);
    }
    setPaywallMessage(chatThreadModalMessage);
    setShowPaywallModal(true);
  };

  const triggerSearch = async (q) => {
    if (!q || q.length < 2) {
      setMessageResults([]);
      return;
    }
    try {
      const res = await fetch("/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            query SearchUserMessages($term: String!, $limit: Int) {
              searchUserMessages(searchTerm: $term, limit: $limit) {
                id
                message
                createdAt
                threadUuid
                threadTitle
                threadIsAdminTargeted
                previousHumanMessageId
                matchType
              }
            }
          `,
          variables: {
            term: q,
            limit: 20,
          },
        }),
      });
      const json = await res.json();
      setMessageResults(json?.data?.searchUserMessages ?? []);
    } catch (e) {
      setMessageResults([]);
    }
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    // No paywall on this right now.
    if (true) {
      //hasSubscription || isAdmin(userData)) {
      if (v.length >= 2) {
        triggerSearch(v);
      } else {
        setMessageResults([]);
      }
    } else {
      // Gate behind paywall
      if (isMobile) {
        setIsSidebarOpen(false);
      }
      setPaywallMessage(chatThreadModalMessage); // or watchlistPaywallModalMessage if preferred
      setShowPaywallModal(true);
    }
  };

  const triggerUserSearch = async (q) => {
    if (!q || q.length < 2) {
      setTargetResults([]);
      return;
    }
    try {
      const res = await fetch("/graphql", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            query AdminSearchUsers($term: String!) {
              admin {
                searchUsers(searchTerm: $term) {
                  id
                  email
                  firstName
                  lastName
                }
              }
            }
          `,
          variables: { term: q },
        }),
      });
      const json = await res.json();
      setTargetResults(json?.data?.admin?.searchUsers ?? []);
    } catch (e) {
      setTargetResults([]);
    }
  };

  const handleTargetOpen = () => {
    setTargetModalOpen(true);
    setTargetError("");
  };

  const handleTargetClose = () => {
    setTargetModalOpen(false);
    setTargetSearch("");
    setTargetResults([]);
    setSelectedTarget(null);
    setTargetMessage("");
    setTargetTitle("");
    setTargetError("");
    setTargetSending(false);
    setTargetCreateAdminCopy(true);
  };

  const handleTargetSend = async () => {
    if (!selectedTarget?.id) {
      setTargetError("Select a user to target.");
      return;
    }
    if (!targetMessage.trim()) {
      setTargetError("Message can't be blank.");
      return;
    }
    setTargetSending(true);
    setTargetError("");
    try {
      const createdThread = await adminCreateTargetedThread({
        userId: selectedTarget.id,
        message: targetMessage.trim(),
        title: targetTitle.trim() || null,
        createAdminCopy: targetCreateAdminCopy,
      });
      if (targetCreateAdminCopy && createdThread?.threadUuid) {
        setThreads((prev) => {
          const safePrev = Array.isArray(prev) ? prev : [];
          return [
            createdThread,
            ...safePrev.filter(
              (t) => t.threadUuid !== createdThread.threadUuid,
            ),
          ];
        });
      }
      handleTargetClose();
    } catch (e) {
      setTargetError(e?.message || "Failed to create targeted thread.");
    } finally {
      setTargetSending(false);
    }
  };

  const handleSelectMessage = (row) => {
    const threadUuid = row?.threadUuid;
    if (!threadUuid) return;
    const switchingAway =
      threadUuid !== activeThread?.threadUuid && threadUuid !== null;
    if (switchingAway && loadingResponse && !canSwitchThreads) {
      setShowThreadSwitchModal(true);
      return;
    }
    setSidebarScrollToThreadUuid(threadUuid);
    const existingThread = threads.find((t) => t.threadUuid === threadUuid);
    const nextThread = existingThread || {
      threadUuid,
      title: row?.threadTitle || "Chat",
      isAdminTargeted: row?.threadIsAdminTargeted ?? false,
    };
    setSearchQuery("");
    setMessageResults([]);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
    const isThreadTitleMatch = row?.matchType === "thread_title";
    if (isThreadTitleMatch) {
      setScrollToMessageId(null);
      selectActiveThread(nextThread);
    } else {
      const targetMessageId = row.previousHumanMessageId || row.id;
      setScrollToMessageId(targetMessageId);
      selectActiveThread(nextThread, targetMessageId);
    }
  };

  const handleNewChatClick = () => {
    if (loadingResponse && !canSwitchThreads) {
      setShowThreadSwitchModal(true);
      return;
    }
    handleAddThread();
  };

  const actions = []

  if (isAdminUser) {
    actions.push({
      icon: <PersonSearchIcon sx={{ color: isDarkMode ? "white" : "black" }} />,
      label: "Message User",
      action: handleTargetOpen,
    });
  }

  actions.push({
    icon: (
      <BookmarkBorderIcon sx={{ color: isDarkMode ? "white" : "black" }} />
    ),
    label: "Watchlist",
    action: preAccountUser ? handleOpenSignUpModal : openWatchlist,
  })

  actions.push({
    icon: <ShowChartIcon sx={{ color: isDarkMode ? "white" : "black" }} />,
    label: "Trade Actions",
    action: preAccountUser ? handleOpenSignUpModal : openTradeProposals,
  });
  actions.push(
    {
      icon: (
        <PsychologyOutlinedIcon
          sx={{ color: isDarkMode ? "white" : "black" }}
        />
      ),
      label: "User Memory",
      action: preAccountUser ? handleOpenSignUpModal : openMemory,
    },
    {
      icon: (
        <AddCircleOutlineIcon sx={{ color: isDarkMode ? "white" : "black" }} />
      ),
      label: "New Chat",
      action: preAccountUser
        ? handleOpenSignUpModal
        : // : (hasSubscription || isAdmin(userData))
          true
          ? handleNewChatClick
          : handleOpenPaywallModal,
    },
  );

  const renderActionRow = ({ icon, label, action }) => {
    const isComingSoon = false;
    return (
      <Box
        key={label}
        onClick={isComingSoon ? undefined : action}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: isComingSoon ? "default" : "pointer",
          mb: 1,
          p: 1,
          borderRadius: 2,
          color: isComingSoon
            ? isDarkMode
              ? "#aaa"
              : "#666"
            : "inherit",
          "&:hover": {
            backgroundColor: isComingSoon
              ? "transparent"
              : isDarkMode
                ? "#333"
                : "rgba(217, 226, 248, 0.7)",
          },
        }}
      >
        {icon}
        <Typography
          variant="subtitle1"
          sx={{ color: isDarkMode ? "white" : "black" }}
        >
          {label} {isComingSoon && <ComingSoonBadge size="sm" />}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Modal open={targetModalOpen} onClose={handleTargetClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(560px, 92vw)",
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: isDarkMode ? "#1B1B1B" : "#FFFFFF",
            color: isDarkMode ? "#F5F5F5" : "#111111",
            borderRadius: 2,
            border: `1px solid ${isDarkMode ? "#2f2f2f" : "#e5e7eb"}`,
            boxShadow: 24,
            p: 3,
            outline: "none",
          }}
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Message User
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, mb: 2 }}
          >
            Create a new thread in the selected user's chat list.
          </Typography>

          <Box sx={{ position: "relative", mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by email or user ID…"
              value={targetSearch}
              onChange={(e) => {
                const v = e.target.value;
                setTargetSearch(v);
                setSelectedTarget(null);
                if (v.length >= 2) {
                  triggerUserSearch(v);
                } else {
                  setTargetResults([]);
                }
              }}
              onFocus={() => {
                if (targetSearch.length >= 2) {
                  triggerUserSearch(targetSearch);
                }
              }}
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "none",
                spellCheck: "false",
              }}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? "#2A2A2A" : "#F6F6F6",
                },
                "& .MuiInputBase-input": {
                  color: isDarkMode ? "#E6E6E6" : "#1D1D1F",
                },
              }}
            />

            {targetResults.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  left: 0,
                  right: 0,
                  backgroundColor: isDarkMode ? "#131521" : "#F6F6F6",
                  borderRadius: 1,
                  boxShadow: 3,
                  zIndex: 20,
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                {targetResults.map((row) => (
                  <Box
                    key={row.id}
                    onClick={() => {
                      setSelectedTarget(row);
                      setTargetSearch(
                        row.email || `${row.firstName || ""} ${row.lastName || ""}`.trim()
                      );
                      setTargetResults([]);
                    }}
                    sx={{
                      px: 2,
                      py: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      color: isDarkMode ? "#F6F6F6" : "#1D1D1F",
                      "&:hover": {
                        backgroundColor: isDarkMode ? "#2a2f3a" : "#e0e0e0",
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.email}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: 11, opacity: 0.7 }}>
                      {row.firstName} {row.lastName} · ID {row.id}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {selectedTarget && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: isDarkMode ? "rgba(59,130,246,0.18)" : "rgba(59,130,246,0.12)",
                border: `1px solid ${isDarkMode ? "rgba(59,130,246,0.4)" : "rgba(59,130,246,0.35)"}`,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Selected: {selectedTarget.email}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 12, opacity: 0.75 }}>
                {selectedTarget.firstName} {selectedTarget.lastName} · ID {selectedTarget.id}
              </Typography>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={targetCreateAdminCopy}
                onChange={(e) => setTargetCreateAdminCopy(e.target.checked)}
                sx={{
                  color: isDarkMode ? "#94A3B8" : "#6B7280",
                  "&.Mui-checked": { color: "#3B82F6" },
                }}
              />
            }
            label="Add this thread to my chats"
            sx={{ mb: 1, color: isDarkMode ? "#E5E7EB" : "#111827" }}
          />

          <TextField
            fullWidth
            label="Message"
            value={targetMessage}
            onChange={(e) => setTargetMessage(e.target.value)}
            multiline
            minRows={3}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": {
                borderRadius: 2,
                backgroundColor: isDarkMode ? "#2A2A2A" : "#F6F6F6",
              },
              "& .MuiInputBase-input": {
                color: isDarkMode ? "#E6E6E6" : "#1D1D1F",
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#B0B0B0" : "#6B7280",
              },
            }}
          />

          <TextField
            fullWidth
            label="Thread Title (optional)"
            value={targetTitle}
            onChange={(e) => setTargetTitle(e.target.value)}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": {
                borderRadius: 2,
                backgroundColor: isDarkMode ? "#2A2A2A" : "#F6F6F6",
              },
              "& .MuiInputBase-input": {
                color: isDarkMode ? "#E6E6E6" : "#1D1D1F",
              },
              "& .MuiInputLabel-root": {
                color: isDarkMode ? "#B0B0B0" : "#6B7280",
              },
            }}
          />

          {targetError && (
            <Typography variant="body2" sx={{ color: "#EF4444", mb: 2 }}>
              {targetError}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="text"
              onClick={handleTargetClose}
              sx={{ color: isDarkMode ? "#E6E6E6" : "#111827" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleTargetSend}
              disabled={targetSending}
              sx={{
                backgroundColor: "#3B82F6",
                "&:hover": { backgroundColor: "#2563EB" },
              }}
            >
              {targetSending ? "Sending..." : "Send"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Box sx={{ mt: 1, position: "relative", px: 2, mb: 1 }}>
        <TextField
          disabled={loadingResponse}
          fullWidth
          size="small"
          placeholder="Search your messages…"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => {
            if (
              (hasSubscription || isAdmin(userData)) &&
              searchQuery.length >= 2
            ) {
              triggerSearch(searchQuery);
            }
          }}
          onBlur={() => setTimeout(() => setMessageResults([]), 150)}
          inputProps={{
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "none",
            spellCheck: "false",
            inputMode: "search",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isDarkMode ? "white" : "black" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputBase-input:hover": loadingResponse && {
              cursor: "not-allowed",
            },
            // container
            "& .MuiInputBase-root": {
              borderRadius: 2,
              backgroundColor: isDarkMode ? "#3B3B3B" : "#F6F6F6",
            },
            // 2) Input text color (lighter gray in dark mode)
            "& .MuiInputBase-input": {
              color: isDarkMode ? "#E6E6E6" : "#1D1D1F",
            },
            // placeholder color
            "& .MuiInputBase-input::placeholder": {
              color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
              opacity: 1, // ensure color above is used
            },
            // (Optional) suppress Chrome autofill yellow background if it ever appears
            "& input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0px 1000px transparent inset",
              WebkitTextFillColor: isDarkMode ? "#E6E6E6" : "#1D1D1F",
              transition: "background-color 9999s ease-in-out 0s",
            },
          }}
        />

        {/* Suggestions dropdown */}
        {messageResults.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              right: 0,
              backgroundColor: isDarkMode ? "#131521" : "#F6F6F6",
              borderRadius: 1,
              boxShadow: 3,
              zIndex: 10,
              maxHeight: 440,
              overflowY: "auto",
            }}
          >
            {messageResults.map((row, i) => (
              <Box
                key={row.id}
                onClick={() => handleSelectMessage(row)}
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  color: isDarkMode ? "#F6F6F6" : "#1D1D1F",
                  "&:hover": {
                    backgroundColor: isDarkMode ? "#2a2f3a" : "#e0e0e0",
                    cursor: "pointer",
                  },
                  ...(i === 0 && {
                    borderTopLeftRadius: 1,
                    borderTopRightRadius: 1,
                  }),
                  ...(i === messageResults.length - 1 && {
                    borderBottomLeftRadius: 1,
                    borderBottomRightRadius: 1,
                  }),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {row.matchType === "thread_title"
                    ? row.threadTitle || "Chat"
                    : getMessageSnippet(row.message, searchQuery)}
                </Typography>
                {row.matchType !== "thread_title" && (
                  <Typography variant="body2" sx={{ fontSize: 11, opacity: 0.7 }}>
                    {row.threadTitle || "Chat"}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontSize: 11, opacity: 0.7 }}>
                  {row.createdAt
                    ? new Date(row.createdAt).toLocaleString()
                    : ""}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, mb: 1 }}>
        {actions.map(renderActionRow)}
      </Box>

      <UpgradeMaxModal
        open={showThreadSwitchModal}
        onClose={() => setShowThreadSwitchModal(false)}
        onUpgrade={() => {
          setShowThreadSwitchModal(false);
          navigate("/plan-selection");
        }}
        title="Get more active chats with MAX."
        description="Your current chat is still answering. Upgrade to Max to have multiple active chats running and switch back and forth at any time."
        // bullets={[
        //   "Multiple simultaneous queries (switch between threads)",
        //   "Autonomous trade tracking and notifications",
        // ]}
        footer="Upgrade when you're ready for advanced multi-thread workflows."
      />
    </>
  );
};

export default SidebarAction;
